// Iter236 · Mapa de contratación por provincia (grid CCAA, no geo real —
// mismo patrón que CoverageMap de Delegados). 3 métricas intercambiables:
// nº licitaciones, importe € y win-rate honesto. Click en una provincia
// filtra el Grafo (pasa los valores crudos de `provincia` vía $in).
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { Loader2, MapPin } from "lucide-react";

// Agrupación CCAA → provincias (nombres canónicos, deben coincidir con
// `backend/services/es_geo.py::PROVINCIA_TO_CCAA`).
export const CCAA_GROUPS = {
  "Andalucía":            ["Almería", "Cádiz", "Córdoba", "Granada", "Huelva", "Jaén", "Málaga", "Sevilla"],
  "Aragón":               ["Huesca", "Teruel", "Zaragoza"],
  "Asturias":             ["Asturias"],
  "Baleares":             ["Baleares"],
  "Canarias":             ["Las Palmas", "Santa Cruz de Tenerife"],
  "Cantabria":            ["Cantabria"],
  "Castilla-La Mancha":   ["Albacete", "Ciudad Real", "Cuenca", "Guadalajara", "Toledo"],
  "Castilla y León":      ["Ávila", "Burgos", "León", "Palencia", "Salamanca", "Segovia", "Soria", "Valladolid", "Zamora"],
  "Cataluña":             ["Barcelona", "Girona", "Lleida", "Tarragona"],
  "C. Valenciana":        ["Alicante", "Castellón", "Valencia"],
  "Extremadura":          ["Badajoz", "Cáceres"],
  "Galicia":              ["A Coruña", "Lugo", "Ourense", "Pontevedra"],
  "La Rioja":             ["La Rioja"],
  "Madrid":               ["Madrid"],
  "Murcia":               ["Murcia"],
  "Navarra":              ["Navarra"],
  "País Vasco":           ["Álava", "Guipúzcoa", "Vizcaya"],
  "Ceuta y Melilla":      ["Ceuta", "Melilla"],
};

const METRICS = [
  { key: "n_licitaciones", label: "Nº licitaciones" },
  { key: "importe_total",  label: "Importe €" },
  { key: "win_rate",       label: "Win-rate %" },
];

export const fmtEurCompact = (v) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 1, notation: "compact" }).format(v || 0);
const fmtEurFull = (v) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v || 0);

function metricValue(item, metric) {
  if (!item) return null;
  if (metric === "win_rate") return item.win_rate?.pct;
  return item[metric];
}

function tileClass(metric, value, max) {
  if (value == null) return "bg-slate-50 text-slate-300 border-slate-100";
  if (metric === "win_rate") {
    if (value >= 70) return "bg-emerald-600 text-white border-emerald-700";
    if (value >= 50) return "bg-emerald-400 text-white border-emerald-500";
    if (value >= 30) return "bg-amber-400 text-amber-950 border-amber-500";
    return "bg-rose-500 text-white border-rose-600";
  }
  const ratio = max > 0 ? value / max : 0;
  if (ratio > 0.66) return "bg-[#007934] text-white border-[#00602a]";
  if (ratio > 0.33) return "bg-[#007934]/65 text-white border-[#007934]/75";
  if (ratio > 0.12) return "bg-[#007934]/30 text-emerald-900 border-[#007934]/35";
  if (ratio > 0) return "bg-[#007934]/12 text-emerald-800 border-[#007934]/20";
  return "bg-slate-50 text-slate-400 border-slate-100";
}

/**
 * @param {string} [dg] - "TRATAMIENTO" | "SERVICIOS" | undefined (todas)
 * @param {(rawValuesCsv: string, provincia: string) => void} [onSelectProvince]
 * @param {boolean} [compact] - grid más denso (usado dentro de Waste Treatment)
 */
export function ProvinceHeatMap({ dg, onSelectProvince, compact }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [metric, setMetric] = useState("n_licitaciones");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const qs = dg && dg !== "all" ? `?dg=${dg}` : "";
    axios.get(`${API}/tender-intelligence/province-map${qs}`)
      .then(r => { if (active) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [dg]);

  const byProv = useMemo(() => {
    const m = {};
    (data?.items || []).forEach(i => { m[i.provincia] = i; });
    return m;
  }, [data]);

  const maxVal = useMemo(() => {
    if (!data || metric === "win_rate") return 100;
    return Math.max(1, ...data.items.map(i => metricValue(i, metric) || 0));
  }, [data, metric]);

  if (loading) return <div className="flex items-center justify-center py-16 text-slate-400" data-testid="ti-province-map-loading"><Loader2 size={22} className="animate-spin"/></div>;
  if (!data) return <div className="text-sm text-slate-400 text-center py-10">No se pudo cargar el mapa de provincias.</div>;

  const selectedItem = selected ? byProv[selected] : null;

  return (
    <div data-testid="ti-province-map">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-1.5" data-testid="ti-map-metric-toggle">
          {METRICS.map(m => (
            <button key={m.key} onClick={() => setMetric(m.key)}
              data-testid={`ti-map-metric-${m.key}`}
              className={`text-[11px] font-semibold px-2.5 py-1 rounded-lg border transition ${metric === m.key ? "bg-[#007934] text-white border-[#007934]" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
              {m.label}
            </button>
          ))}
        </div>
        <div className="text-[11px] text-slate-500 font-mono" data-testid="ti-map-totales">
          {data.totales.n_licitaciones} licit. · {fmtEurCompact(data.totales.importe_total)}
          {data.sin_provincia > 0 && <span className="text-slate-400"> · {data.sin_provincia} sin provincia</span>}
        </div>
      </div>

      {selectedItem && (
        <div className="mb-3 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 flex items-center justify-between gap-3 flex-wrap" data-testid="ti-province-detail-card">
          <div className="flex items-center gap-2 text-xs">
            <MapPin size={13} className="text-indigo-600"/>
            <b className="text-slate-800">{selected}</b>
            <span className="text-slate-500">{selectedItem.n_licitaciones} licit. · {fmtEurFull(selectedItem.importe_total)} · {selectedItem.win_rate.label}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onSelectProvince?.(selectedItem.raw_values.join(","), selected)}
              className="text-[11px] font-semibold px-2.5 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700"
              data-testid="ti-province-filter-graph-btn"
            >
              Ver en grafo
            </button>
            <button onClick={() => setSelected(null)} className="text-[11px] text-slate-400 hover:text-rose-600" data-testid="ti-province-detail-close">✕</button>
          </div>
        </div>
      )}

      <div className={`grid gap-2 ${compact ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4" : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-6"}`} data-testid="ti-province-map-grid">
        {Object.entries(CCAA_GROUPS).map(([ccaa, provs]) => (
          <div key={ccaa} className="border border-slate-200 rounded-lg p-2 bg-slate-50/50">
            <div className="text-[10px] font-bold text-slate-500 uppercase mb-1 truncate">{ccaa}</div>
            <div className="flex flex-wrap gap-1">
              {provs.map(p => {
                const item = byProv[p];
                const val = metricValue(item, metric);
                const cls = tileClass(metric, val, maxVal);
                const isActive = selected === p;
                const label = metric === "win_rate" && item ? (item.win_rate.pct == null ? "n/a" : `${item.win_rate.pct}%`)
                  : metric === "importe_total" && item ? fmtEurCompact(item.importe_total)
                  : item ? item.n_licitaciones : "";
                return (
                  <button key={p}
                    onClick={() => setSelected(isActive ? null : p)}
                    title={`${p}${item ? ` · ${item.n_licitaciones} licit. · ${fmtEurFull(item.importe_total)} · ${item.win_rate.label}` : " · sin datos"}`}
                    data-testid={`ti-province-tile-${p.replace(/\s+/g, "-")}`}
                    className={`text-[10px] font-semibold px-2 py-1 rounded border transition ${cls} ${isActive ? "ring-2 ring-offset-1 ring-indigo-500" : ""}`}>
                    <div>{p}</div>
                    {item && <div className="text-[9px] opacity-80 font-mono">{label}</div>}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
